import { allProducts, productCategories as fallbackCategories } from "@/mocks/productsData";

export const allCategoryLabel = "全部";

/**
 * 'Display' = 展示型商品：前台僅展示，不顯示加入購物車與數量選擇。
 * 'Sale' = 銷售型商品：前台顯示加入購物車與數量選擇。
 */
export type ProductType = "Display" | "Sale";
export type ProductUsageType = "Sale" | "Trial" | "WarrantyReplacement" | "Gift" | "ServicePart" | "Consumable" | "Test";

/** 多規格屬性樣板，例如 { name: "顏色", values: ["香檳金", "曜石黑"] }。 */
export type VariantAttribute = {
  name: string;
  values: string[];
};

/** 一筆規格組合（SKU），各自擁有價格、庫存、圖片。 */
export type ProductVariant = {
  id: number;
  sku: string;
  optionValues: Record<string, string>;
  optionLabel: string;
  price: number;
  salePrice: number | null;
  stock: number;
  imageUrl: string | null;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
};

export type Product = {
  id: number;
  name: string;
  slug?: string;
  subtitle: string;
  productCode: string;
  barcode?: string | null;
  productCategoryId?: number | null;
  productType: ProductType;
  productUsageType?: ProductUsageType;
  allowZeroPriceOrder?: boolean;
  hasVariants: boolean;
  price: number;
  salePrice?: number | null;
  /** 單一規格商品的庫存數量；null 表示不追蹤庫存（不限量）。多規格商品請改看各 variant 的 stock。 */
  stock: number | null;
  category: string;
  tags: string[];
  shortDescription: string;
  image: string;
  galleryImages?: string[];
  description: string;
  featureItems?: ProductFeatureItem[];
  specItems?: ProductSpecItem[];
  quickInfoItems?: ProductQuickInfoItem[];
  variantAttributes: VariantAttribute[];
  variants: ProductVariant[];
  /** 規格組合總數（後端於列表 API 提供的輕量欄位，避免列表頁需要載入完整 variants 陣列才能顯示組數）。 */
  variantCount: number;
  /** 庫存示警燈號：'ok' 正常／'low' 偏低／'out' 已無庫存。單一規格看 stock，多規格看所有啟用中規格裡最低的庫存數。 */
  stockStatus: "ok" | "low" | "out";
  isFeatured?: boolean;
  isNew?: boolean;
  isHot?: boolean;
  isPublished?: boolean;
  sortOrder?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};

/** 商品是否可購買（銷售型且尚有可販售的規格/庫存）用於前台判斷是否顯示購物車區塊。 */
export function isSellableProduct(product: Product) {
  return product.productType === "Sale";
}

/** 取得商品目前應顯示的「起始」價格資訊：多規格取最低價的組合，單一規格取商品本身價格。 */
export function getDisplayPriceRange(product: Product) {
  if (product.hasVariants && product.variants.length > 0) {
    const activeVariants = product.variants.filter((variant) => variant.isActive);
    const pool = activeVariants.length > 0 ? activeVariants : product.variants;
    const prices = pool.map((variant) => variant.salePrice ?? variant.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return { min, max, isRange: min !== max };
  }
  return { min: product.salePrice ?? product.price, max: product.salePrice ?? product.price, isRange: false };
}

export type ProductFeatureItem = {
  Title: string;
  Description: string;
};

export type ProductSpecItem = {
  Label: string;
  Value: string;
};

export type ProductQuickInfoItem = {
  Icon: string;
  Label: string;
  Value: string;
};

export type ProductCategory = {
  id: number;
  name: string;
  description?: string | null;
  parentCategoryId?: number | null;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type VariantAttributePayload = {
  Name: string;
  Values: string[];
};

export type ProductVariantPayload = {
  Sku: string;
  OptionValues: Record<string, string>;
  Price: number;
  SalePrice: number | null;
  Stock: number;
  ImageUrl: string | null;
  IsDefault: boolean;
  IsActive: boolean;
  SortOrder: number;
};

export type ProductPayload = {
  Name: string;
  Slug: string;
  Subtitle: string;
  ProductCode: string;
  Barcode: string;
  ProductCategoryId: number | null;
  ProductType: ProductType;
  ProductUsageType: ProductUsageType;
  AllowZeroPriceOrder: boolean;
  HasVariants: boolean;
  Price: number;
  SalePrice: number | null;
  Stock: number | null;
  Tags: string[];
  ShortDescription: string;
  MainImageUrl: string;
  GalleryImageUrls: string[];
  Description: string;
  FeatureItems: ProductFeatureItem[];
  SpecItems: ProductSpecItem[];
  QuickInfoItems: ProductQuickInfoItem[];
  VariantAttributes: VariantAttributePayload[];
  Variants: ProductVariantPayload[];
  IsFeatured: boolean;
  IsNew: boolean;
  IsHot: boolean;
  IsPublished: boolean;
  SortOrder: number;
};

export type ProductCategoryPayload = {
  Name: string;
  Description: string;
  ParentCategoryId: number | null;
  IsActive: boolean;
  SortOrder: number;
};

type ApiVariantAttribute = {
  Name: string;
  Values: string[];
};

type ApiVariant = {
  ProductVariantId: string | number;
  Sku: string;
  OptionValues: Record<string, string>;
  OptionLabel: string;
  Price: number | string;
  SalePrice?: number | string | null;
  Stock: number | string;
  ImageUrl?: string | null;
  IsDefault?: boolean;
  IsActive?: boolean;
  SortOrder?: number | string;
};

type ApiProduct = {
  ProductId: string | number;
  Name: string;
  Slug?: string;
  Subtitle: string;
  ProductCode?: string;
  Barcode?: string | null;
  ProductCategoryId?: string | number | null;
  Category: string;
  ProductType?: string;
  ProductUsageType?: string;
  AllowZeroPriceOrder?: boolean;
  HasVariants?: boolean;
  Price: number | string;
  SalePrice?: number | string | null;
  Stock?: number | string | null;
  Tags?: string[];
  ShortDescription?: string;
  MainImageUrl: string;
  GalleryImageUrls?: string[];
  Description: string;
  FeatureItems?: ProductFeatureItem[];
  SpecItems?: ProductSpecItem[];
  QuickInfoItems?: ProductQuickInfoItem[];
  VariantAttributes?: ApiVariantAttribute[];
  Variants?: ApiVariant[];
  VariantCount?: number;
  StockStatus?: string;
  IsFeatured?: boolean;
  IsNew?: boolean;
  IsHot?: boolean;
  IsPublished?: boolean;
  SortOrder?: number | string;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

type ApiCategory = {
  ProductCategoryId: string | number;
  Name: string;
  Description?: string | null;
  ParentCategoryId?: string | number | null;
  IsActive?: boolean;
  SortOrder?: number | string;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

const adminCsrfKey = "fulling_admin_csrf";

function getAdminCsrfToken() {
  return sessionStorage.getItem(adminCsrfKey) || "";
}

function normalizeVariant(variant: ApiVariant): ProductVariant {
  return {
    id: Number(variant.ProductVariantId),
    sku: variant.Sku || "",
    optionValues: variant.OptionValues && typeof variant.OptionValues === "object" ? variant.OptionValues : {},
    optionLabel: variant.OptionLabel || "",
    price: Number(variant.Price || 0),
    salePrice: variant.SalePrice === null || variant.SalePrice === undefined || variant.SalePrice === "" ? null : Number(variant.SalePrice),
    stock: Number(variant.Stock || 0),
    imageUrl: variant.ImageUrl || null,
    isDefault: variant.IsDefault === true,
    isActive: variant.IsActive !== false,
    sortOrder: Number(variant.SortOrder || 100),
  };
}

function normalizeVariantAttribute(attribute: ApiVariantAttribute): VariantAttribute {
  return {
    name: attribute.Name || "",
    values: Array.isArray(attribute.Values) ? attribute.Values : [],
  };
}

function normalizeProductUsageType(value?: string): ProductUsageType {
  return value === "Trial" || value === "WarrantyReplacement" || value === "Gift" || value === "ServicePart" || value === "Consumable" || value === "Test" ? value : "Sale";
}

function normalizeProduct(product: ApiProduct): Product {
  return {
    id: Number(product.ProductId),
    name: product.Name || "",
    slug: product.Slug || String(product.ProductId),
    subtitle: product.Subtitle || "",
    productCode: product.ProductCode || "",
    barcode: product.Barcode || null,
    productCategoryId: product.ProductCategoryId ? Number(product.ProductCategoryId) : null,
    productType: product.ProductType === "Display" ? "Display" : "Sale",
    productUsageType: normalizeProductUsageType(product.ProductUsageType),
    allowZeroPriceOrder: product.AllowZeroPriceOrder === true,
    hasVariants: product.HasVariants === true,
    price: Number(product.Price || 0),
    salePrice: product.SalePrice === null || product.SalePrice === undefined || product.SalePrice === "" ? null : Number(product.SalePrice),
    stock: product.Stock === null || product.Stock === undefined || product.Stock === "" ? null : Number(product.Stock),
    category: product.Category || "",
    tags: Array.isArray(product.Tags) ? product.Tags : [],
    shortDescription: product.ShortDescription || "",
    image: product.MainImageUrl || "",
    galleryImages: Array.isArray(product.GalleryImageUrls) ? product.GalleryImageUrls : [],
    description: product.Description || "",
    featureItems: Array.isArray(product.FeatureItems) ? product.FeatureItems : [],
    specItems: Array.isArray(product.SpecItems) ? product.SpecItems : [],
    quickInfoItems: Array.isArray(product.QuickInfoItems) ? product.QuickInfoItems : [],
    variantAttributes: Array.isArray(product.VariantAttributes) ? product.VariantAttributes.map(normalizeVariantAttribute) : [],
    variants: Array.isArray(product.Variants) ? product.Variants.map(normalizeVariant) : [],
    variantCount: Array.isArray(product.Variants) ? product.Variants.length : Number(product.VariantCount || 0),
    stockStatus: product.StockStatus === "low" || product.StockStatus === "out" ? product.StockStatus : "ok",
    isFeatured: product.IsFeatured === true,
    isNew: product.IsNew === true,
    isHot: product.IsHot === true,
    isPublished: product.IsPublished !== false,
    sortOrder: Number(product.SortOrder || 0),
    createdAt: product.CreatedAt || null,
    updatedAt: product.UpdatedAt || null,
  };
}

function normalizeCategory(category: ApiCategory): ProductCategory {
  return {
    id: Number(category.ProductCategoryId),
    name: category.Name || "",
    description: category.Description || "",
    parentCategoryId: category.ParentCategoryId ? Number(category.ParentCategoryId) : null,
    isActive: category.IsActive !== false,
    sortOrder: Number(category.SortOrder || 0),
    createdAt: category.CreatedAt || null,
    updatedAt: category.UpdatedAt || null,
  };
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "資料處理失敗，請稍後再試。");
  }
  return data as T;
}

function adminHeaders(json = true) {
  return {
    Accept: "application/json",
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(getAdminCsrfToken() ? { "X-CSRF-Token": getAdminCsrfToken() } : {}),
  };
}

export async function fetchPublicProducts() {
  try {
    const data = await fetch("/api/products", {
      credentials: "include",
      headers: { Accept: "application/json" },
    }).then((response) => parseJsonResponse<{ ok: boolean; Products?: ApiProduct[]; Categories?: string[] }>(response));

    const products = (data.Products || []).map(normalizeProduct);
    return {
      products,
      categories: data.Categories?.length ? [allCategoryLabel, ...data.Categories.filter((category) => category !== allCategoryLabel)] : [allCategoryLabel],
    };
  } catch {
    return {
      products: allProducts,
      categories: fallbackCategories,
    };
  }
}

export async function fetchPublicProduct(productId: string | number) {
  const data = await fetchPublicProductDetail(productId);
  return data.product;
}

export async function fetchPublicProductDetail(productId: string | number) {
  try {
    const data = await fetch(`/api/products/${productId}`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    }).then((response) => parseJsonResponse<{ ok: boolean; Product: ApiProduct; RelatedProducts?: ApiProduct[] }>(response));

    return {
      product: normalizeProduct(data.Product),
      relatedProducts: (data.RelatedProducts || []).map(normalizeProduct),
    };
  } catch {
    const fallbackProducts = allProducts.map((product) => ({ ...product, slug: String(product.id) }));
    const product = fallbackProducts.find((item) => item.id === Number(productId) || item.slug === String(productId)) || null;
    const relatedProducts = product
      ? fallbackProducts
          .filter((item) => item.id !== product.id)
          .sort((a, b) => {
            const aCategoryMatch = a.category === product.category ? 0 : 1;
            const bCategoryMatch = b.category === product.category ? 0 : 1;
            if (aCategoryMatch !== bCategoryMatch) return aCategoryMatch - bCategoryMatch;
            return Math.abs((a.salePrice || a.price) - (product.salePrice || product.price)) - Math.abs((b.salePrice || b.price) - (product.salePrice || product.price));
          })
          .slice(0, 3)
      : [];
    return { product, relatedProducts };
  }
}

export async function fetchFeaturedProducts(limit = 6) {
  const data = await fetchPublicProducts();
  const featured = data.products.filter((product) => product.isFeatured !== false).slice(0, limit);
  return featured.length > 0 ? featured : data.products.slice(0, limit);
}

export async function listAdminProducts(params: { Status?: string; Category?: string; CategoryId?: string; Keyword?: string; StockLevel?: "low" | "out"; Page?: number; PerPage?: number }) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Category) query.set("Category", params.Category);
  if (params.CategoryId) query.set("CategoryId", params.CategoryId);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.StockLevel) query.set("StockLevel", params.StockLevel);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const data = await fetch(`/api/admin/products${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{
    ok: boolean;
    Products: ApiProduct[];
    Counts?: { Published: number; Hidden: number };
    StockCounts?: { Low: number; Out: number };
    Categories?: string[];
    Pagination: { Page: number; PerPage: number; Total: number; TotalPages: number };
  }>(response));

  return {
    ...data,
    Products: data.Products.map(normalizeProduct),
  };
}

export async function getAdminProduct(productId: number | string) {
  const data = await fetch(`/api/admin/products/${productId}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{ ok: boolean; Product: ApiProduct }>(response));
  return normalizeProduct(data.Product);
}

export async function saveAdminProduct(productId: number | null, payload: ProductPayload) {
  const path = productId ? `/api/admin/products/${productId}` : "/api/admin/products";
  const method = productId ? "PUT" : "POST";
  const data = await fetch(path, {
    method,
    credentials: "include",
    headers: adminHeaders(true),
    body: JSON.stringify(payload),
  }).then((response) => parseJsonResponse<{ ok: boolean; Product: ApiProduct }>(response));
  return normalizeProduct(data.Product);
}

export async function deleteAdminProduct(productId: number) {
  return fetch(`/api/admin/products/${productId}`, {
    method: "DELETE",
    credentials: "include",
    headers: adminHeaders(false),
  }).then((response) => parseJsonResponse<{ ok: boolean }>(response));
}

export async function uploadAdminProductImage(file: File) {
  const formData = new FormData();
  formData.append("Image", file);
  const data = await fetch("/api/admin/products/upload-image", {
    method: "POST",
    credentials: "include",
    headers: adminHeaders(false),
    body: formData,
  }).then((response) => parseJsonResponse<{ ok: boolean; Url: string }>(response));
  return data.Url;
}

export async function deleteAdminProductImage(url: string) {
  return fetch("/api/admin/products/upload-image", {
    method: "DELETE",
    credentials: "include",
    headers: adminHeaders(true),
    body: JSON.stringify({ Url: url }),
  }).then((response) => parseJsonResponse<{ ok: boolean }>(response));
}

export async function listAdminProductCategories() {
  const data = await fetch("/api/admin/product-categories", {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{ ok: boolean; Categories: ApiCategory[] }>(response));
  return data.Categories.map(normalizeCategory);
}

export async function saveAdminProductCategory(categoryId: number | null, payload: ProductCategoryPayload) {
  const path = categoryId ? `/api/admin/product-categories/${categoryId}` : "/api/admin/product-categories";
  const method = categoryId ? "PUT" : "POST";
  const data = await fetch(path, {
    method,
    credentials: "include",
    headers: adminHeaders(true),
    body: JSON.stringify(payload),
  }).then((response) => parseJsonResponse<{ ok: boolean; Category: ApiCategory }>(response));
  return normalizeCategory(data.Category);
}

export async function deleteAdminProductCategory(categoryId: number) {
  return fetch(`/api/admin/product-categories/${categoryId}`, {
    method: "DELETE",
    credentials: "include",
    headers: adminHeaders(false),
  }).then((response) => parseJsonResponse<{ ok: boolean }>(response));
}
